<template class="app">
  <teleport to="head">
    <MetaTags
      title="Website to IP Lookup"
      description="Find the IP addresses for any website or domain name. This online tool shows both IPv4 and IPv6 addresses as well as their hosting provider and location."
      :url="`https://www.nslookup.io/website-to-ip-lookup/`"
    />
  </teleport>
  <HeaderComponent :domain-search-to-url="(encodedDomain) => `/domains/${encodedDomain}/webservers/`" search-button-text="Find IP addresses" />
  <div class="bg-neutral-100">
    <main class="container mx-auto">
      <div class="flex flex-col justify-center max-w-lg min-h-[calc(100vh-250px)] mx-auto text-center">
        <form class="text-base flex flex-col items-center justify-center" autocomplete="off">
          <h1 class="font-extrabold text-4xl max-w-full my-8">Website to IP lookup</h1>
          <DomainSearch
            class="h-12 mb-6 max-w-full w-[500px]"
            autofocus
            emphasize
            buttonText="Find IP addresses"
            placeholder-text="URL or domain name"
            @submitted="redirectTo" />
          <p class="mt-8 mb-20 text-center max-w-full h-20 w-[500px] text-neutral-800">Find all IP addresses with this website to IP checker. For example, try
            <dns-records-link page="webservers" domain="facebook.com" class="text-blue-600 hover:text-blue-800 hover:underline" /> or <dns-records-link page="webservers" domain="google.com" class="text-blue-600 hover:text-blue-800 hover:underline" /> to view their IP addresses. You can also enter a URL to get its IP addresses.
          </p>
          <AdUnit class="mt-8 mx-auto" :config="WebsiteToIpLookup" />
        </form>
      </div>
      <div class="prose max-w-6xl prose-indigo mt-6 mx-auto bg-white p-6 rounded-t shadow">
        <div class="flex flex-col justify-between md:flex-row">
          <h2 class="inline-block text-2xl mt-0">How to lookup a website's IP address</h2>
          <span class="shrink-0 flex items-center mb-5 text-sm font-medium leading-4">
            <span class="hidden mr-2 sm:inline">Jump to</span>
            <router-link v-for="platform in platforms" :key="platform" :to="`#${platform.toLowerCase()}`" class="no-underline mr-2 last:mr-0">
              <button type="button" class="inline-flex items-center rounded-md border border-[#3a0161] bg-white px-3 py-2 text-[#3a0161] shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                <icon :icon="platform" class="mr-2" /><span class="first-letter:uppercase">{{ platform }}</span>
              </button>
            </router-link>
          </span>
        </div>
        <div class="md:grid md:gap-6 md:grid-cols-2">
          <div>
            <p>Every website has at least one IP address connected to its domain name. For example, the website Facebook has, among others,
              the IP address <code>157.240.214.35</code> connected to the domain name <code>www.facebook.com</code>.</p>
            <p>When you enter a URL in your browser, it will first determine the domain name of that URL. It then looks up the IP address and
              connects to the server with this IP. Once connected, it fetches the website's source code to display the page.</p>
            <p>These IP addresses are configured in the Domain Name System (DNS) as <router-link to="/learning/dns-record-types/a/">A</router-link> and <router-link to="/learning/dns-record-types/aaaa/">AAAA</router-link> records. But how can you look up those IP addresses? That depends on
              the operating system you are using. Windows, Linux and Mac all have a different way of finding the IPv4 and IPv6 addresses. The
              sections below list the instructions for each operating system.</p>
          </div>
          <img class="w-full md:mb-0 md:mt-6" src="img/pages/record-type-lookup/a-lookup-mac.png" alt="Example output of an IPv4 address lookup" />
        </div>
        <h2 id="windows">How to lookup a website's IP address on Windows</h2>
        <p>To check the server IP addresses for a domain name on Windows, follow these steps:</p>
        <ol>
          <li>Open a <em>command prompt</em> by navigating to <strong> <icon icon="click" /> Start &rarr; <icon icon="click" />
            'Type here to search' &rarr; <icon icon="type" />
            'cmd' &rarr; <icon icon="click" /> Open</strong>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/record-type-lookup/open-command-prompt-windows.png" alt="Open a command prompt in Windows" /></li>
          <li>Type <strong><icon icon="type" /> <code>nslookup -q=A example.com</code></strong> and hit <strong>
            <icon icon="type" /> [enter]</strong> to get the IPv4 addresses for <code>example.com</code>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/record-type-lookup/a-lookup-windows.png" alt="IPv4 address lookup in Windows" /></li>
          <li>The IP addresses are listed below the <strong>Non-authoritative answer</strong> heading.</li>
          <li>(Optional) To also see the IPv6 addresses, repeat these steps with <code>AAAA</code> instead of <code>A</code>.</li>
        </ol>
        <h2 id="mac">How to lookup a website's IP address on Mac OS</h2>
        <p>To check the IP addresses for a domain name on a Mac, follow these steps:</p>
        <ol>
          <li>Open a <em>terminal</em> by entering <strong> <icon icon="type" /> [command] + [space] &rarr; <icon icon="type" /> 'terminal.app'
            &rarr; <icon icon="type" /> [enter]</strong>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/record-type-lookup/open-terminal-mac.png" alt="Open a terminal on a Mac" /></li>
          <li>Type <strong><icon icon="type" /> <code>dig example.com A</code></strong> and hit <strong>
            <icon icon="type" /> [enter]</strong> to get the IPv4 addresses for <code>example.com</code>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/record-type-lookup/a-lookup-mac.png" alt="IPv4 lookup on a Mac" />
          </li>
          <li>The IP addresses are listed below the <strong>ANSWER SECTION</strong> heading.</li>
          <li>(Optional) To also see the IPv6 addresses, repeat these steps with <code>AAAA</code> instead of <code>A</code>.</li>
        </ol>
        <h2 id="linux">How to lookup a website's IP address on Linux</h2>
        <p>To check the IP addresses for a domain name on Linux, follow these steps:</p>
        <ol>
          <li>Open a <em>terminal</em> by entering <strong> <icon icon="type" /> [Super] &rarr; <icon icon="type" /> 'terminal' &rarr;
            <icon icon="type" /> [enter]</strong>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/record-type-lookup/open-terminal-linux.png" alt="Open a terminal in Linux" /></li>
          <li>Type <strong><icon icon="type" /> <code>dig example.com A</code></strong> and hit <strong>
            <icon icon="type" /> [enter]</strong> to get the IPv4 addresses for <code>example.com</code>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/record-type-lookup/a-lookup-linux.png" alt="IPv4 lookup in Linux" /></li>
          <li>The IP addresses are listed below the <strong>ANSWER SECTION</strong> heading.</li>
          <li>(Optional) To also see the IPv6 addresses, repeat these steps with <code>AAAA</code> instead of <code>A</code>.</li>
        </ol>
        <h2>How to find a website's IP address</h2>
        <p>To check the IP addresses of a domain, follow these steps:</p>
        <ol>
          <li>Open the <router-link to="/website-to-ip-lookup/">website to IP lookup tool</router-link>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/website-to-ip-lookup/website-to-ip-lookup.png" alt="Open the website to IP lookup tool" /></li>
          <li>Enter the URL or domain name and hit <strong><icon icon="type" /> [enter]</strong>.<img loading="lazy" class="block my-2 w-full border border-black/10 rounded-md shadow md:w-1/2" src="img/pages/website-to-ip-lookup/website-to-ip-lookup-results.png" alt="Example IP addresses" /></li>
          <li>The tool will query the IPv4 and IPv6 addresses and shown them at the top of the page.</li>
        </ol>
      </div>
    </main>
  </div>
  <FooterComponent />
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import Icon from '@/components/micro/IconComponent.vue';
import MetaTags from '@/components/MetaTags.vue';
import { LargeRectangle, MediumRectangle } from '@/common/adSizes';
import AdUnit from '@/components/ads/AdUnit.vue';
import { domainNameToUrlPath } from '@/common/domainName';
import { WebsiteToIpLookup } from '@/common/adSlots';

const router = useRouter();

const platforms: Array<'windows' | 'mac' | 'linux'> = [
  'windows',
  'mac',
  'linux',
];

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/webservers/`);
}
</script>
